import { IVButtonDropDownItem } from "@directives/_core/v-button/v-button.component";
import { IAutocompleteMatch } from "@directives/_forms/autocomplete/autocomplete.component";
import { VButtonStatus } from "@directives/_forms/v-input/v-input.component";
import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IDriver } from "@interfaces/IDriver";
import { IGeofence } from "@interfaces/IGeofences";
import { IGoogleMapsMarker } from "@interfaces/IGoogleMapsMarkerOptions";
import { IVehicle } from "@interfaces/IVehicle";
import { DisplayService } from "@services/display.service";
import { GeofenceService } from "@services/geofence.service";
import { NavigationService } from "@services/navigation.service";
import { PreferencesService } from "@services/preferences.service";
import { VeriDate } from "@services/veriDate.service";
import { TmsModuleNavigation } from "@tms/tms.navigation";
import { RouteVisitStatus } from "@tms/_enums/RouteVisitStatus";
import { IGeoArea } from "@tms/_models/IGeoArea";
import { IPlannedRoute } from "@tms/_models/IPlannedRoute";
import { IPlannedRouteVisit } from "@tms/_models/IPlannedRouteVisit";
import { ITmsRouteRules } from "@tms/_models/ITmsRouteRules";
import { PlannedRouteVisitService } from "@tms/_services/planned-route-visit.service";
import { IPlannedRouteDetail, PlannedRouteService } from "@tms/_services/planned-route.service";
import { RouteMatchingRuleLabels, SchedulingService } from "@tms/_services/scheduling.service";
import * as angular from "angular";
import { copy } from "angular";
import { IPolyline } from "../../../../../../../../../scripts/typings/google.maps.angular";
import { IRouteVisitsUpdate } from "../scheduleVisitsList/scheduleVisitsList.component";
import "./scheduleEditor.component.scss";

interface IComponentBindings {
    routeId?: number;
    route?: IPlannedRouteDetail;
}

export class ScheduleEditorComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    routeId: number = -1;
    route: IPlannedRouteDetail;

    dirtyRoute: IPlannedRoute;
    displayRoute: IPlannedRoute;

    // raw data
    geofences: IGeofence[];
    geoAreas: IGeoArea[];
    isShowingTemplates: boolean;

    // form data
    isFormValid: boolean;
    startDate: Date;
    startMoment: moment.Moment;
    selectedVehicle: IVehicle;
    selectedVehicleMatch: IAutocompleteMatch;
    selectedDriver: IDriver;
    selectedDriverMatch: IAutocompleteMatch;

    // map
    veriMap: IVeriMapComponent;
    mapMarkers: IGoogleMapsMarker[];
    routePolyline: IPolyline;

    // start & endoptions
    startRuleDropdownItems: IVButtonDropDownItem<string>[];
    startRouteMatchingRuleLabels: RouteMatchingRuleLabels;
    startingOptionsProperty: string;
    startOptionsLabel: string;
    showingStartOptions: boolean;

    endRuleDropdownItems: IVButtonDropDownItem<string>[];
    endRouteMatchingRuleLabels: RouteMatchingRuleLabels;
    endingOptionsProperty: string;
    endOptionsLabel: string;
    showingEndOptions: boolean;

    cachedRouteRules: ITmsRouteRules;

    VButtonStatus = VButtonStatus;

    lastForcedVisitUpdate: number; // used to force visits list change, detecting update without copying model

    preferences: ICombinedPreferences;

    // spinners
    loading: boolean;
    calculatingRoute: boolean;

    static $inject = ["$q", "$scope", "displayService", "navigationService", "plannedRouteService", "plannedRouteVisitService", "schedulingService", "veriDate", "geofenceService", "preferencesService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $scope: angular.IScope,
        private readonly displayService: DisplayService,
        private readonly navigationService: NavigationService,
        private readonly plannedRouteService: PlannedRouteService,
        private readonly plannedRouteVisitService: PlannedRouteVisitService,
        private readonly schedulingService: SchedulingService,
        private readonly veriDate: VeriDate,
        private readonly geofenceService: GeofenceService,
        private readonly preferencesService: PreferencesService
    ) {
    }

    $onInit(): void {
        this.loading = true;

        this.updateAvailableRules();

        const promises: angular.IPromise<any>[] = [];

        promises.push(this.preferencesService.getAllPreferences());
        promises.push(this.geofenceService.getGeofences());

        this.$q.all(promises).then(([preferences, geofences]) => {
            this.geofences = geofences;
            this.preferences = preferences;
        }).finally(() => {
            this.prepareRoute(this.routeId, this.route).finally(() => {

                this.plannedRouteService.sortRouteVisitsByPlannedArrivalTime(this.dirtyRoute);
                this.updateRouteRules();

                if (!this.dirtyRoute.PlannedVisits?.length) {
                    this.addDefaultVisits();
                }

                this.cacheRouteRules(this.dirtyRoute.TmsRouteRules);
                this.setDefaultStartMoment();
                this.geoAreas = this.plannedRouteService.extractGeoAreasFromRoute(this.route);

                this.updateDisplayRoute(this.dirtyRoute);

                this.loading = false;
            });
        });

    }

    onClickedStartRuleDropdown(item: IVButtonDropDownItem<string>): void {
        this.startingOptionsProperty = item.data;

        this.updateRouteRules();
    }

    onClickedEndRuleDropdown(item: IVButtonDropDownItem<string>): void {
        this.endingOptionsProperty = item.data;

        this.updateRouteRules();
    }

    onCalculatingRoute(calculating: boolean): void {
        this.calculatingRoute = calculating;
    }

    onVisitsListUpdate(update: IRouteVisitsUpdate): void {
        this.dirtyRoute.PlannedVisits = update.visits;

        this.validate();
        this.refreshMap(update.fitMap);

        this.$scope.$applyAsync();
    }

    onExternalReferenceChanged(text: string): void {
        this.dirtyRoute.ExternalReference = text;

        this.validate();
    }

    cancel(): void {
        this.loading = true;

        this.plannedRouteService.cancelPlannedRoute(this.dirtyRoute).then((cancelledRoute: IPlannedRoute) => {
            this.navigationService.go(TmsModuleNavigation.Scheduling);
        }).finally(() => {
            this.loading = false;
        });
    }

    save(): void {
        if (!this.validate()) {
            console.error("Form invalid, exiting save method");
            return;
        }

        this.loading = true;

        const saveModel = this.createModelForSaving(this.dirtyRoute);

        this.plannedRouteService.updatePlannedRoute(saveModel).then((savedRoute: IPlannedRoute) => {

            this.loading = false;

            if (!savedRoute.Id) {
                return;
            }

            this.navigationService.go(TmsModuleNavigation.Scheduling);
        });
    }

    onStartMomentChanged(startMoment: moment.Moment): void {
        this.updateStartMoment(startMoment);
    }

    onExternalReferenceChange(): void {

    }

    onDateFilterChanged(filter: IDateFilter): void {
        const newStartMoment = this.veriDate.createMoment(filter.start);

        if (this.startMoment) {
            newStartMoment.set("hour", this.startMoment.hour());
            newStartMoment.set("minute", this.startMoment.minute());
        }

        this.updateStartMoment(newStartMoment);
    }

    updateStartMoment(newStartMoment: moment.Moment): void {
        let previousStartMoment = null;

        if (this.startMoment) {
            previousStartMoment = this.veriDate.createMoment(this.startMoment).clone();
        }

        this.startMoment = newStartMoment;
        this.startDate = this.startMoment.toDate();

        if (!this.dirtyRoute) {
            return;
        }

        this.updatePlannedStartEndTime(this.dirtyRoute, newStartMoment, previousStartMoment);
    }

    updateStartMomentUsingTimespan(timeSpan: string): void {
        if (!timeSpan) {
            return;
        }

        const hour = Number(timeSpan.substring(0, timeSpan.indexOf(":")));
        const minute = Number(timeSpan.substring(timeSpan.indexOf(":") + 1, timeSpan.length));

        if (!hour || !minute) {
            return;
        }

        const newStartMoment = this.veriDate.createMoment(this.startMoment.toDate());
        newStartMoment.set("hour", +hour);
        newStartMoment.set("minute", +minute);

        this.updateStartMoment(newStartMoment);
    }

    onMatchedVehicleChange(match: IAutocompleteMatch): void {
        this.dirtyRoute.VehicleId = null;
        this.selectedVehicle = null;
        this.selectedVehicleMatch = null;

        if (!match?.object) {
            return;
        }

        this.dirtyRoute.VehicleId = match.objectId;
        this.selectedVehicle = match.object;
        this.selectedVehicleMatch = match;

        if (!this.selectedVehicle.GeofenceId) {
            return;
        }

        const geofence = this.geofences.find(geofence => geofence.Id === this.selectedVehicle.GeofenceId);

        if (!geofence) {
            return;
        }

        let triggerChange = false;

        // set start location to geofence of selected vehicle, if no current location
        if (this.dirtyRoute.TmsRouteRules.RouteMatchingRules.StartRouteOnDepartStartingLocation) {
            const firstLocation = this.dirtyRoute.PlannedVisits[0];

            if (!firstLocation.RouteStopLocation) {
                firstLocation.Name = geofence.Description;
                firstLocation.GeofenceId = geofence.Id;
                firstLocation.Geofence = geofence;
                firstLocation.RouteStopLocation = geofence;

                triggerChange = true;
            }
        }

        // set end location to geofence of selected vehicle, if no current location
        if (this.dirtyRoute.TmsRouteRules.RouteMatchingRules.EndRouteOnArriveAtEndingLocation) {
            const lastLocation = this.dirtyRoute.PlannedVisits[this.dirtyRoute.PlannedVisits.length - 1];

            if (!lastLocation.RouteStopLocation) {
                lastLocation.Name = geofence.Description;
                lastLocation.GeofenceId = geofence.Id;
                lastLocation.Geofence = geofence;
                lastLocation.RouteStopLocation = geofence;

                triggerChange = true;
            }
        }

        if (triggerChange) {
            this.validate();

            this.lastForcedVisitUpdate = Date.now();
        }
    }

    onMatchedDriverChange(match: IAutocompleteMatch): void {
        this.dirtyRoute.DriverId = null;
        this.selectedDriver = null;
        this.selectedDriverMatch = null;

        if (!match?.object) {
            return;
        }

        this.dirtyRoute.DriverId = match.objectId;
        this.selectedDriver = match.object;
        this.selectedDriverMatch = match;
    }

    onVeriMapLoaded(veriMap: IVeriMapComponent): void {
        this.veriMap = veriMap;

        this.refreshMap(true);
    }

    onReturnToStart(): void {
        this.enableRouteRule("EndRouteOnArriveAtEndingLocation");
        this.cacheRouteRules(this.dirtyRoute.TmsRouteRules);
        this.updateAvailableRules();

        this.validate();
    }

    toggleShowStartOptions(): void {
        this.showingStartOptions = !this.showingStartOptions;
    }

    toggleShowEndOptions(): void {
        this.showingEndOptions = !this.showingEndOptions;
    }

    // ---

    private updatePlannedStartEndTime(route: IPlannedRoute, newStartMoment: moment.Moment, previousStartMoment?: moment.Moment): void {
        route.PlannedStartTime = newStartMoment.toDate();

        if (!route.PlannedVisits?.length) {
            this.validate();
            return;
        }

        const difference = this.veriDate.getDiff(newStartMoment, previousStartMoment, "days");

        route.PlannedVisits.forEach(visit => {
            if (visit.PlannedArrivalTime) {
                visit.PlannedArrivalTime = this.veriDate.createMoment(visit.PlannedArrivalTime).clone().subtract(difference, "days").toDate();
            }

            if (visit.PlannedDepartureTime) {
                visit.PlannedDepartureTime = this.veriDate.createMoment(visit.PlannedDepartureTime).clone().subtract(difference, "days").toDate();
            }

            this.plannedRouteVisitService.updateTimeProperties(visit);
        });

        route.PlannedEndTime = route.PlannedVisits[route.PlannedVisits.length - 1].PlannedDepartureTime;

        this.validate();
    }

    private setMandatoryRules(route: IPlannedRoute): void {
        route.TmsRouteRules.RouteMatchingRules.MinimumStartTime = this.veriDate.createMoment(this.startMoment).clone().subtract(this.preferences.MinimumStartTime.Value, "minutes").toDate();
        route.TmsRouteRules.VisitMatchingRules.MaximumTimeToRestartRoute = this.veriDate.convertMinsToTimeSpan(this.preferences.MaximumTimeToRestartRoute.Value, true);

        route.TmsRouteRules.RouteExpiryRules.MaximumStartTime = this.veriDate.createMoment(route.PlannedStartTime).clone().add(this.preferences.MaximumStartTime.Value, "minutes").toDate();
        route.TmsRouteRules.RouteExpiryRules.MaximumExpiryTime = this.veriDate.createMoment(route.PlannedEndTime).clone().add(this.preferences.MaximumExpiryTime.Value, "minutes").toDate();
        route.TmsRouteRules.RouteExpiryRules.MaximumLateTimeAllowed = this.veriDate.convertMinsToTimeSpan(this.preferences.MaximumLateTimeAllowed.Value);
    }

    private prepareRoute(routeId: number, route?: IPlannedRouteDetail): angular.IPromise<void> {
        if (route) {
            this.dirtyRoute = copy(this.route);

            return this.$q.resolve();
        }

        if (this.routeId === undefined || routeId === -1) {
            this.route = this.schedulingService.createRouteTemplate();
            this.dirtyRoute = copy(this.route);

            return this.$q.resolve();
        }

        return this.plannedRouteService.getPlannedRoute(routeId, { mapDrivers: true, mapVehicles: true, mapGeofences: true }).then(plannedRoute => {
            this.schedulingService.setVisitsAsDirty(plannedRoute.PlannedVisits);
            this.route = plannedRoute;
            this.dirtyRoute = copy(this.route);
        });
    }

    private setDefaultStartMoment(): void {
        if (!!this.route?.PlannedStartTime) {
            this.startMoment = this.veriDate.createMoment(this.route.PlannedStartTime);
        } else {
            this.startMoment = this.veriDate.createMoment(new Date()).add(15, "m");
        }

        this.updateStartMoment(this.startMoment);
    }

    private refreshMap(fitMap: boolean = false): void {
        if (!this.dirtyRoute) {
            return;
        }

        this.refreshRouteStats();
        this.geoAreas = this.plannedRouteService.extractGeoAreasFromRoute(this.route);

        if (!fitMap) {
            return;
        }

        this.schedulingService.fitMapToVisits(this.veriMap, this.dirtyRoute.PlannedVisits);
    }

    private updateRouteRules(): void {
        if (!this.dirtyRoute.TmsRouteRules) {
            this.dirtyRoute.TmsRouteRules = this.schedulingService.createDefaultTmsRouteRules();
        }

        if (!this.dirtyRoute.TmsRouteRules.RouteMatchingRules) {
            this.dirtyRoute.TmsRouteRules.RouteMatchingRules = this.schedulingService.createDefaultRouteMatchingRules();
        }

        if (!this.dirtyRoute.TmsRouteRules.VisitMatchingRules) {
            this.dirtyRoute.TmsRouteRules.VisitMatchingRules = this.schedulingService.createDefaultVisitMatchingRules();
        }

        if (!this.dirtyRoute.TmsRouteRules.RouteExpiryRules) {
            this.dirtyRoute.TmsRouteRules.RouteExpiryRules = this.schedulingService.createDefaultRouteExpiryRules();
        }

        if (!this.startingOptionsProperty) {
            this.startingOptionsProperty = this.schedulingService.getActiveStartOptionProperty(this.dirtyRoute.TmsRouteRules)
                || this.startRouteMatchingRuleLabels[0];
        }

        if (!this.endingOptionsProperty) {
            this.endingOptionsProperty = this.schedulingService.getActiveEndOptionProperty(this.dirtyRoute.TmsRouteRules)
                || this.endRouteMatchingRuleLabels[0];
        }

        this.clearAvailableStartRulesModel();
        this.clearAvailableEndRulesModel();
        this.enableRouteRule(this.startingOptionsProperty);
        this.enableRouteRule(this.endingOptionsProperty);
        this.cacheRouteRules(this.dirtyRoute.TmsRouteRules);
        this.updateAvailableRules();

        this.validate();

        this.$scope.$applyAsync();
    }

    private enableRouteRule(ruleKey: string): void {
        if (!ruleKey) {
            return;
        }

        this.dirtyRoute.TmsRouteRules.RouteMatchingRules[ruleKey] = true;

        if (ruleKey.includes("Start")) {
            this.startingOptionsProperty = ruleKey;

        } else if (ruleKey.includes("End")) {
            this.endingOptionsProperty = ruleKey;
        }
    }

    private clearAvailableStartRulesModel(): void {
        this.dirtyRoute.TmsRouteRules.RouteMatchingRules.StartRouteOnArriveAnyVisitLocation = false;
        this.dirtyRoute.TmsRouteRules.RouteMatchingRules.StartRouteOnDepartAnyVisitLocation = false;
        this.dirtyRoute.TmsRouteRules.RouteMatchingRules.StartRouteOnDepartStartingLocation = false;
        this.dirtyRoute.TmsRouteRules.RouteMatchingRules.StartRouteOnJourneyStart = false;
    }

    private clearAvailableEndRulesModel(): void {
        this.dirtyRoute.TmsRouteRules.RouteMatchingRules.EndRouteOnAllVisitsArrived = false;
        this.dirtyRoute.TmsRouteRules.RouteMatchingRules.EndRouteOnAllVisitsDeparted = false;
        this.dirtyRoute.TmsRouteRules.RouteMatchingRules.EndRouteOnArriveAtEndingLocation = false;
        this.dirtyRoute.TmsRouteRules.RouteMatchingRules.EndRouteOnDepartAtEndingLocation = false;
    }

    private cacheRouteRules(rules: ITmsRouteRules): void {
        this.cachedRouteRules = copy(rules);
    }

    private updateAvailableRules(): void {
        this.startRouteMatchingRuleLabels = this.schedulingService.getStartRouteMatchingRuleLabels();
        this.endRouteMatchingRuleLabels = this.schedulingService.getEndRouteMatchingRuleLabels();

        this.startRuleDropdownItems = [];
        this.endRuleDropdownItems = [];

        Object.keys(this.startRouteMatchingRuleLabels).forEach((ruleProperty: string, index: number) => {
            const selected: boolean = ruleProperty === this.startingOptionsProperty;

            this.startRuleDropdownItems.push({
                label: this.startRouteMatchingRuleLabels[ruleProperty],
                data: ruleProperty,
                disabled: selected,
                icon: (selected) ? "done" : null
            });
        });

        Object.keys(this.endRouteMatchingRuleLabels).forEach((ruleProperty: string, index: number) => {
            const selected: boolean = ruleProperty === this.endingOptionsProperty;

            this.endRuleDropdownItems.push({
                label: this.endRouteMatchingRuleLabels[ruleProperty],
                data: ruleProperty,
                disabled: selected,
                icon: (selected) ? "done" : null
            });
        });
    }

    private refreshRouteStats(): void {
        this.plannedRouteService.formatRouteForDisplay(this.dirtyRoute, this.geofences);
    }

    private validate(): boolean {
        const validation = this.schedulingService.validateVisitList(this.dirtyRoute.PlannedVisits);

        this.isFormValid = validation.valid;
        this.updateDisplayRoute(this.dirtyRoute);

        return validation.valid;
    }

    private addDefaultVisits(): void {
        this.dirtyRoute.PlannedVisits = [this.schedulingService.createPlannedVisitTemplate()];

        if (this.dirtyRoute.TmsRouteRules.RouteMatchingRules.StartRouteOnDepartStartingLocation) {
            this.dirtyRoute.PlannedVisits.push(this.schedulingService.createPlannedVisitTemplate());
        }

        if (this.dirtyRoute.TmsRouteRules.RouteMatchingRules.EndRouteOnArriveAtEndingLocation) {
            this.dirtyRoute.PlannedVisits.push(this.schedulingService.createPlannedVisitTemplate());
        }
    }

    private createModelForSaving(dirtyRoute: IPlannedRoute): IPlannedRoute {
        this.updatePlannedStartEndTime(this.dirtyRoute, this.startMoment);
        this.setMandatoryRules(this.dirtyRoute);

        const routeCopy: IPlannedRoute = copy(this.dirtyRoute);
        const isNewRoute: boolean = routeCopy.Id === -1;

        if (isNewRoute) {
            delete routeCopy.Id;
        }

        routeCopy.PlannedVisits?.forEach(visit => {
            if (isNewRoute || visit.Id < 0) {
                delete visit.Id;
            }
        });

        const cancelledVisits: IPlannedRouteVisit[] = this.route.PlannedVisits?.filter(originalVisit => !this.dirtyRoute.PlannedVisits?.find(visit => visit.Id === originalVisit.Id));

        if (cancelledVisits?.length) {
            cancelledVisits.forEach(visit => visit.Status = RouteVisitStatus.Cancelled);
            routeCopy.PlannedVisits.push(...cancelledVisits);
        }

        routeCopy.TmsRouteRulesJson = JSON.stringify(routeCopy.TmsRouteRules);

        return routeCopy;
    }

    private updateDisplayRoute(route: IPlannedRoute): void {
        this.displayRoute = copy(route);
    }
}

export class ScheduleEditorComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            routeId: "<?",
            route: "<?"
        };

        this.controller = ScheduleEditorComponentController;
        this.template = require("./scheduleEditor.component.html");
    }
}
